import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useMyAuth } from '../../context/MyAuthContext';
import { apiFetchJson } from './api';

export default function MyLogin() {
  const navigate = useNavigate();
  const { user, login } = useMyAuth();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Rediriger si déjà connecté
  useEffect(() => {
    if (user) {
      navigate('/my/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Traitement du callback OAuth (?code=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    setProcessing(true);
    setError('');
    const redirectUri = `${window.location.origin}/my`;
    let cancelled = false;

    (async () => {
      try {
        const data = await apiFetchJson('/api/my/auth/google-callback', {
          method: 'POST',
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        }, 'Erreur de connexion');

        if (!cancelled) {
          window.history.replaceState(null, '', '/my');
          login({ email: data.email, name: data.name, picture: data.picture, role: data.role });
          navigate('/my/dashboard', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          window.history.replaceState(null, '', '/my');
        }
      } finally {
        if (!cancelled) setProcessing(false);
      }
    })();

    return () => { cancelled = true; };
  }, [login, navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const redirectUri = `${window.location.origin}/my`;
      const data = await apiFetchJson(
        `/api/my/auth/google-url?redirect_uri=${encodeURIComponent(redirectUri)}`,
        {},
        'Erreur de connexion',
      );
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-[#0d0f14] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#0099D6] animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Connexion en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0f14] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#0099D6] flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">METERIS</h1>
          <p className="text-sm text-gray-400 mt-1">Espace d'administration</p>
        </div>

        <div className="bg-[#15171e] rounded-2xl border border-white/5 p-6 space-y-5">
          <p className="text-sm text-gray-400 text-center">
            Connectez-vous avec votre compte Google pour accéder à l'espace d'administration.
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-semibold rounded-xl py-3.5 text-sm hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            )}
            Se connecter avec Google
          </button>

          <p className="text-[10px] text-gray-600 text-center">
            L'accès est ouvert à tous. Les droits sont attribués par l'administrateur.
          </p>
        </div>
      </div>
    </div>
  );
}
